import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import {
  saveParcelPrefs,
  selectParcelPrefs,
  setParcelPrefs,
 } from '../../ducks/userProperties'
import withStore from '../withStore'
import { hiddenMailingAddessFields } from '../Parcels/propertySettings'
import {   getCachedParcelPrefs,
  fetchAndCacheParcelPrefs,
  getUsersPreference } from '../../common/utils/UserPreferences'

const PropertyDetailsCustomizer = ({
  availableSchemas = [],
  groupedFields = {},
  groupTitles = {},
  subgroupTitles = {},
  schema = {},
  schemaKey = 'united_states',
}) => {
  const dispatch = useDispatch()
  const parcelPrefs = useSelector(selectParcelPrefs)
  const [expandedPanels, setExpandedPanels] = useState(() => {
    // initialize panel states
    const initial = {}
    Object.keys(groupedFields).forEach(key => {
      initial[key] = false // or true for open by default
    })
    return initial
  })
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const bulkUpdatingRef = useRef(false)

  // Load preferences on mount
  useEffect(() => {
    let cancelled = false

    const cached = getCachedParcelPrefs()
    if(cached) {
      dispatch(setParcelPrefs(cached))
    } else {
      ;(async () => {
        try {
          const prefs = await fetchAndCacheParcelPrefs(getUsersPreference)
          if(!cancelled) dispatch(setParcelPrefs(prefs || {}))
        } catch (err) {
          console.warn('failed to load preferences', err)
          if(!cancelled) dispatch(setParcelPrefs({}))
        }
      })()
    }

    return () => { cancelled = true }
  }, [dispatch])

  const handleSchemaChange = (value) => {
    // Submit hidden form to change schema
    const form = document.getElementById('schema-switch-form')
    const input = document.getElementById('schema-key-input')
    if(input) input.value = value
    if(form) form.submit()
  }

  const togglePanel = (groupKey) => {
    setExpandedPanels(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }))
  }

  const getGroupFields = (groupItems) => {
    const fields = []
    groupItems.forEach(item => {
      if(typeof item === 'string') {
        fields.push(item)
      } else if(typeof item === 'object') {
        Object.values(item).forEach(subgroupFields => {
          fields.push(...subgroupFields)
        })
      }
    })
    return fields
  }

  const getGroupStatus = (groupKey, fields) => {
    // Special case for additional_items - check the group preference
    if(groupKey === 'additional_items') {
      return (parcelPrefs[groupKey] !== false && parcelPrefs[groupKey] !== 'false') ? 'all' : 'none'
    }
    if(fields.length === 0) {
      // For empty groups, check the group key itself
      return parcelPrefs[groupKey] ? 'all' : 'none'
    }

    const checkedCount = fields.filter(field =>
      parcelPrefs[field] !== false && parcelPrefs[field] !== 'false'
    ).length

    if(checkedCount === fields.length) return 'all'
    if(checkedCount === 0) return 'none'
    return 'partial'
  }

  const getStatusIconClass = (status) => {
    switch (status) {
      case 'all':
        return 'fa fa-check text-success'
      case 'none':
        return 'fa fa-times text-danger'
      case 'partial':
        return 'fa fa-minus'
      default:
        return 'fa'
    }
  }

const handleFieldChange = (field, checked) => {
  if(bulkUpdatingRef.current) return
  const updatedPrefs = { ...parcelPrefs, [field]: checked }
  dispatch(saveParcelPrefs(updatedPrefs))
}

const handleGroupToggle = (groupKey, fields) => {
  if(fields.length === 0) {
    const newState = !parcelPrefs[groupKey]
    const updatedPrefs = { ...parcelPrefs, [groupKey]: newState }
    dispatch(saveParcelPrefs(updatedPrefs))
    return
  }

  const allChecked = fields.every(field =>
    parcelPrefs[field] !== false && parcelPrefs[field] !== 'false'
  )
  const newState = !allChecked

  bulkUpdatingRef.current = true
  const updates = {}
  fields.forEach(field => {
    updates[field] = newState
  })
  bulkUpdatingRef.current = false

  const updatedPrefs = { ...parcelPrefs, ...updates }
  dispatch(saveParcelPrefs(updatedPrefs))
}

  return (
    <>
      <div className="row">
        <div className="col-md-8">
          <h3>Customize Property View Details</h3>
          <p>Check the boxes next to the fields that you would like to show in &quot;Property Details&quot; when you click on a property.</p>
          <p>Changes are saved automatically.</p>

          {/* Hidden form for schema switching */}
          <form
            id="schema-switch-form"
            className="form-inline"
            method="GET"
            action={window.location.pathname}
          >
            <input
              type="hidden"
              name="schema_key"
              id="schema-key-input"
              value={schemaKey}
            />
          </form>
        </div>
      </div>

      <div className="row">
        <div className="col-md-6">
          <div className="dropdown margin-btm-md">
            <a
              id="action-dropdown-btn"
              className="btn btn-round btn-default btn-sm dropdown-toggle"
              href="#"
              role="button"
              onClick={(e) => {
                e.preventDefault()
                setDropdownOpen(!dropdownOpen)
              }}
              aria-expanded={dropdownOpen}
            >
              <span id="dropdown-label">
                {availableSchemas.find(([_, value]) => value === (schemaKey))?.[0] || 'United States'}
              </span>
              <span className="caret"></span>
            </a>

            {dropdownOpen && (
              <ul className="dropdown-menu" style={{ display: 'block' }}>
                {availableSchemas.map(([label, value]) => (
                  <li key={value}>
                    <a
                      className="dropdown-option"
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        setDropdownOpen(false)
                        handleSchemaChange(value)
                      }}
                    >
                      {label}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div id="customize-details-section" className="margin-btm-lg">
        {Object.entries(groupedFields).map(([groupKey, groupItems]) => {
          const fields = getGroupFields(groupItems)
          const status = getGroupStatus(groupKey, fields)
          const isExpanded = expandedPanels[groupKey]

          return (
            <div className="row" key={groupKey}>
              <div className="col-md-12">
                <div className="panel panel-default margin-btm-md">
                  <div className="panel-heading" role="tab" id={`heading-${groupKey}`}>
                    <a
                      className="width-100 text-decoration-none"
                      href={`#collapse-${groupKey}`}
                      onClick={(e) => {
                        e.preventDefault()
                        togglePanel(groupKey)
                      }}
                      aria-expanded={isExpanded}
                      aria-controls={`collapse-${groupKey}`}
                    >
                      <h4 className="panel-title mb-0">
                        <button className="no-btn width-100" type="button">
                          <span className="pull-left margin-right-lg">
                            <i className={getStatusIconClass(status)} id={`status-icon-${groupKey}`}></i>
                          </span>
                          <i
                            className={`fa ${isExpanded ? 'fa-angle-up' : 'fa-angle-down'} pull-right`}
                            id={`caret-${groupKey}`}
                          ></i>
                          <span className="pull-left">
                            {groupTitles[groupKey] || groupKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                        </button>
                      </h4>
                    </a>
                  </div>

                  <div
                    className={`panel-collapse collapse ${isExpanded ? 'in' : ''}`}
                    id={`collapse-${groupKey}`}
                    role="tabpanel"
                    aria-labelledby={`heading-${groupKey}`}
                    style={{ display: isExpanded ? 'block' : 'none' }}
                  >
                    <div className="panel-body">
                      {groupKey === 'additional_items' ? (
                        <div className="field">
                          <div className="field-label margin-left-lg">
                            <input
                              type="checkbox"
                              name={`${groupKey}_fields[]`}
                              value="true"
                              checked={parcelPrefs[groupKey] !== false && parcelPrefs[groupKey] !== 'false'}
                              onChange={(e) => handleFieldChange(groupKey, e.target.checked)}
                              id={`toggle-${groupKey}-show`}
                              className={`${groupKey}_fields child-checkbox`}
                            />
                            <label htmlFor={`toggle-${groupKey}-show`} className="margin-left-md" >
                              All Additional (non-schema) Fields
                            </label>
                          </div>
                        </div>

                      ) : (
                        <>
                          <div className="field margin-btm-md">
                            <button
                              className="btn btn-sm btn btn-round btn-default"
                              type="button"
                              onClick={() => handleGroupToggle(groupKey, fields)}
                              id={`toggle-${groupKey}-all`}
                            >
                              <span className="toggle-label" id={`label-${groupKey}_group`}>
                                {status === 'all' ? 'Deselect All' : 'Select All'}
                              </span>
                            </button>
                          </div>

                          {groupItems.map((item) => {
                            if(typeof item === 'string') {
                              return (
                                <div className="field" key={item}>
                                  <div className="field-label margin-left-lg">
                                    <input
                                      type="checkbox"
                                      name={`${groupKey}_fields[]`}
                                      value={item}
                                      checked={parcelPrefs[item] !== false && parcelPrefs[item] !== 'false'}
                                      onChange={(e) => handleFieldChange(item, e.target.checked)}
                                      id={`field-${item}-checkbox`}
                                      className={`${groupKey}_fields child-checkbox`}
                                    />
                                    <label htmlFor={`field-${item}-checkbox`} className="margin-left-md">
                                      {schema[item] || `not found: ${item}`}
                                    </label>
                                  </div>
                                </div>
                              )
                            } else if(typeof item === 'object') {
                              return Object.entries(item).map(([subgroupKey, subgroupFields]) => {
                                const filteredFields = subgroupFields.filter(field =>
                                  !hiddenMailingAddessFields.includes(field)
                                )
                                return (
                                  <React.Fragment key={subgroupKey}>
                                    <h5 style={{ marginTop: '1em' }}>
                                      {subgroupTitles[subgroupKey] || subgroupKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                    </h5>
                                    {filteredFields.map(field => (
                                      <div className="field" key={field}>
                                        <div className="field-label margin-left-lg">
                                          <input
                                            type="checkbox"
                                            name={`${subgroupKey}_fields[]`}
                                            value={field}
                                            checked={parcelPrefs[field] !== false && parcelPrefs[field] !== 'false'}
                                            onChange={(e) => handleFieldChange(field, e.target.checked)}
                                            id={`field-${item}-checkbox`}
                                            className={`${groupKey}_fields ${subgroupKey}_fields child-checkbox`}
                                          />
                                          <label htmlFor={`field-${item}-checkbox`} className="margin-left-md">
                                            {schema[field] || `not found: ${field}`}
                                          </label>
                                        </div>
                                      </div>
                                    ))}
                                  </React.Fragment>
                                )}
                              )
                            }
                            return null
                          })}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <div className="row margin-btm-lg"></div>
    </>
  )
}

PropertyDetailsCustomizer.propTypes = {
  availableSchemas: PropTypes.array.isRequired,
  groupedFields:    PropTypes.object.isRequired,
  groupTitles:      PropTypes.object.isRequired,
  subgroupTitles:   PropTypes.object.isRequired,
  schema:           PropTypes.object.isRequired,
  schemaKey:        PropTypes.string,
}

export default withStore(PropertyDetailsCustomizer)